import axios from 'axios';
import {
  CREATE_ENTITY_APPLICATION_ONLY,
  ENTITY_REGISTER,
  GET_ENTITY_TYPE_FORMS,
  LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
} from '@/utils/constants/constants';
import {
  build_assign_options,
  build_child_navigation_query,
  build_child_tab_map,
  build_child_tabs,
  build_sub_entity_payload,
  get_insert_index,
  get_parent_entity_type_id,
  get_validation_error,
  get_visible_tabs_without_child_tabs,
  hide_modal,
  normalize_response_data,
  normalize_sub_entity_item,
  show_modal,
  sync_parent_session,
} from './entity_application_overview_sub_entity_helpers';

export const entity_application_overview_sub_entity_methods = {
  get_assigned_children(parent_entity_type_id) {
    if (!parent_entity_type_id) {
      const empty_rows = [];
      return Promise.resolve(empty_rows);
    }

    return axios.get(LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL, {
      headers: this.headers,
      params: { parent_entity_type_id },
    }).then(response => {
      if (response.data?.status !== "success") {
        const empty_rows = [];
        return empty_rows;
      }

      const payload = response.data?.data;
      const child_rows = Array.isArray(payload)
        ? payload
        : (payload?.assigned_children || []);

      return child_rows;
    }).catch(() => {
      const empty_rows = [];
      return empty_rows;
    });
  },

  async inject_child_entity_type_tabs() {
    const parent_entity_type_id = get_parent_entity_type_id(this);
    if (!parent_entity_type_id) {
      return;
    }

    const assigned_children = await this.get_assigned_children(parent_entity_type_id);
    const child_tabs = build_child_tabs(assigned_children);
    const sub_entities_index = (this.visible_tabs || []).findIndex(tab => this.is_sub_entities_placeholder(tab));
    const visible_tabs = get_visible_tabs_without_child_tabs(this.visible_tabs);

    this.visible_tabs = visible_tabs;
    if (sub_entities_index !== -1) {
      this.visible_tabs.splice(sub_entities_index, 1, ...child_tabs);
    } else {
      this.visible_tabs.splice(get_insert_index(this), 0, ...child_tabs);
    }

    this.child_entity_tab_map = build_child_tab_map(child_tabs);
    this.child_tabs = child_tabs;
    this.visible_tabs = (this.visible_tabs || []).filter(tab => !this.is_sub_entities_placeholder(tab));
  },

  is_child_entity_type_tab(value) {
    return typeof value === "string" && value.startsWith("child-");
  },

  child_type_id_from_tab(tab_id) {
    return this.is_child_entity_type_tab(tab_id) ? Number(String(tab_id).replace("child-", "")) || 0 : 0;
  },

  child_tab_title(tab_id) {
    const tab_map = this.child_entity_tab_map || {};
    const tab = Reflect.get(tab_map, tab_id) || {};
    const tab_name = Reflect.get(tab, "name") || "Sub-Entity";
    return tab_name;
  },

  normalize_item(item) {
    const normalized_item = normalize_sub_entity_item(item);

    return normalized_item;
  },

  child_tab_items(tab_id) {
    const child_type_id = this.child_type_id_from_tab(tab_id);
    const root = Array.isArray(this.pending_applications_list) ? this.pending_applications_list[0] : null;
    const children = Array.isArray(root?.children) ? root.children : [];

    return child_type_id ? children.filter(child => Number(child.entity_type_id) === Number(child_type_id)) : [];
  },

  async ensure_root_children_loaded() {
    const root = Array.isArray(this.pending_applications_list) ? this.pending_applications_list[0] : null;
    if (!root || root._children_loaded) {
      return;
    }

    const children = await this.get_sub_entities(this.application_id, this.entity_profile_id);
    root.children = (children || []).map(child => this.make_node_from_child_entity(child, this.application_id));
    root._children_loaded = true;
    root._children_checked = true;
    root.has_children = root.children.length > 0;
    this.prep_tables();
  },

  async open_add_sub_entity_from_child_tab(tab_id) {
    const success = await this.prepare_assign_modal(this.current_entity);
    if (!success) {
      return;
    }

    const child_type_id = this.child_type_id_from_tab(tab_id);
    const exists = this.assign_sub_entity_options.some(option => Number(option.id) === Number(child_type_id));
    if (exists) {
      this.assign_sub_entity_type_id = child_type_id;
      await this.assign_on_sub_entity_change();
    }

    show_modal('assign_sub_entity_modal');
  },

  async prepare_assign_modal(item) {
    if (!item) {
      this.show_error('No entity selected. Please try again.');
      return false;
    }

    const normalized_item = this.normalize_item(item);
    if (!normalized_item.type_id) {
      return false;
    }

    this.current_form.parent_id = normalized_item.type_id;
    this.current_form.parent_name = normalized_item.entity_type_name || "Entity";
    this.current_form.parent_application_id = normalized_item.application_id || this.application_id;
    this.current_form.parent_entity_profile_id = normalized_item.entity_profile_id || this.entity_profile_id;
    this.assign_sub_entity_type_id = "";
    this.assign_form_list = [];
    this.assign_selected_form_id = "";
    this.assign_entity_name = "";
    this.assign_email = "";
    this.include_email = false;
    this.validation_error = null;
    this.is_saving = false;
    this.is_loading_options = true;

    try {
      this.assign_sub_entity_options = build_assign_options(await this.get_assigned_children(this.current_form.parent_id));
      await this.$nextTick();
      return true;
    } catch (error) {
      this.show_error('Failed to load sub-entity options: ' + error.message);
      return false;
    } finally {
      this.is_loading_options = false;
    }
  },

  handle_folderlist_add(item) {
    this.prepare_assign_modal(item).then(success => {
      if (success) {
        show_modal('assign_sub_entity_modal');
      }
    });
  },

  async assign_on_sub_entity_change() {
    const entity_type_id = Number(this.assign_sub_entity_type_id || 0);
    this.assign_form_list = [];
    this.assign_selected_form_id = '';

    if (!entity_type_id) {
      return;
    }

    try {
      const response = await axios.get(GET_ENTITY_TYPE_FORMS, {
        headers: this.headers,
        params: { entity_type_id }
      });
      const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      this.assign_form_list = Array.isArray(data?.data) ? data.data : [];
    } catch (error) {
      this.show_error('Error getting forms: ' + (error?.message || 'Unknown error'));
    }
  },

  async save_assign_sub_entity() {
    this.validation_error = get_validation_error(this);
    if (this.validation_error) {
      return;
    }

    this.is_saving = true;
    try {
      const payload = build_sub_entity_payload(this);
      const api_url = this.include_email ? ENTITY_REGISTER : CREATE_ENTITY_APPLICATION_ONLY;
      const response = await axios.post(api_url, payload, { headers: this.headers });
      const response_data = normalize_response_data(response, this.include_email);

      if (response_data?.status !== "success") {
        this.validation_error = response_data?.message || "Failed to assign sub-entity.";
        return;
      }

      this.show_success("Sub-entity added successfully!");
      hide_modal('assign_sub_entity_modal');
      await this.reload_component_data();
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Error assigning the sub-entity.";
      this.validation_error = message;
      this.show_error(message);
    } finally {
      this.is_saving = false;
    }
  },

  async fetch_assigned_for_item(item) {
    await this.$options.mixins.find(mixin => mixin.methods && mixin.methods.fetch_assigned_for_item)
      .methods.fetch_assigned_for_item.call(this, item);
  },

  go_to_parent_entity() {
    if (!this.$route?.query?.parent_application_id || !this.$route?.query?.parent_entity_profile_id) {
      this.$router.back();
      return;
    }

    // Reset live component state before navigating on the same route.
    // This prevents RenderSections from firing with a stale child tab/form context
    // against the parent application identifiers during the route transition.
    this.active_master_tab = 'profile';
    this.active_tab = 'Overview';
    this.application_form_id = null;
    this.db_tabs = [];

    sync_parent_session(this);
    this.$router.push({
      path: "/entity_application_overview",
      query: {
        application_id: String(this.$route.query.parent_application_id),
        entity_profile_id: String(this.$route.query.parent_entity_profile_id),
        ...(this.$route.query.parent_user_id ? { user_id: String(this.$route.query.parent_user_id) } : {}),
        entity_type_id: String(this.$route.query.parent_entity_type_id || ""),
        entity_name: this.$route.query.parent_entity_name || "",
        entity_type_name: this.$route.query.parent_entity_type_name || "",
        status: this.$route.query.parent_status || "",
      }
    });
  },

  redirect_to_application_overview(row) {
    const query = build_child_navigation_query(this, row);
    if (!Number(query.application_id)) {
      this.show_error("Unable to find application data");
      return;
    }

    this.$router.push({
      path: "/entity_application_overview",
      query
    });
  },
};
